import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import { ApplicationState } from "stores";
import { createDirtyUser, changeUserField, saveUser } from "stores/user";
import { HooplaUser } from "types";
import { LabeledInput } from "components/Input";
import { ContainerLoader } from "components/Loading";
import Button from "components/Button";
import Typography from "components/Typography";
import PageHeader from "components/PageHeader";
import * as styles from "./styles";
import { useNavigate } from "react-router-dom";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

interface IProps {
  user: HooplaUser;
  loading: boolean;
  patching: boolean;
  error: boolean;
  createDirtyUser: () => void;
  changeUserField: (key: string, value: string) => void;
  saveUser: (userId: number, firstName: string, lastName: string, phoneNumber: string) => void;
}

const Settings: React.FC<IProps> = (props) => {
  const navigate = useNavigate();
  React.useEffect(() => {
    props.createDirtyUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { userId, firstName, lastName, phoneNumber } = props.user;
    props.saveUser(userId, firstName, lastName, phoneNumber);
  };

  const handleChangeField = (key: string, e: React.ChangeEvent<HTMLInputElement>) => {
    props.changeUserField(key, e.target.value);
  };

  const handleChangePhoneNumber = (phoneNumber: string) => {
    props.changeUserField("phoneNumber", phoneNumber);
  };

  const handleCancel = () => {
    navigate("/");
  };

  if (props.loading) {
    return <ContainerLoader />;
  }

  return (
    <React.Fragment>
      <PageHeader>
        <FormattedMessage id="containers.Settings.yourProfile" />
      </PageHeader>

      <form onSubmit={handleSubmit}>
        <div className={styles.row}>
          <LabeledInput
            id="firstName"
            label={<FormattedMessage id="containers.Settings.firstName" />}
            value={props.user.firstName || ""}
            onChange={(e) => handleChangeField("firstName", e)}
          />
          <LabeledInput
            id="lastName"
            label={<FormattedMessage id="containers.Settings.lastName" />}
            value={props.user.lastName || ""}
            onChange={(e) => handleChangeField("lastName", e)}
          />
        </div>
        <div className={styles.row}>
          <LabeledInput
            id="email"
            label={<FormattedMessage id="containers.Settings.email" />}
            value={props.user.email || ""}
            onChange={() => {}}
            disabled={true}
          />
          <div className={styles.labeledInput}>
            <label htmlFor="phoneNumber">
              <FormattedMessage id="containers.Settings.phoneNumber" />
            </label>
            <PhoneInput
              className={styles.phoneNumberInput}
              defaultCountry={process.env.REACT_APP_REGION === "NO" ? "no" : "gb"}
              value={props.user.phoneNumber || ""}
              onChange={(e) => handleChangePhoneNumber(e)}
              inputClassName={styles.phoneInput}
            />
          </div>
        </div>
        <div className={styles.footer}>
          {props.error && (
            <div className={styles.error}>
              <Typography color="red">
                <FormattedMessage id="containers.Settings.genericError" />
              </Typography>
            </div>
          )}

          <div className={styles.actions}>
            <Button variant="outlined" onClick={handleCancel}>
              <FormattedMessage id="containers.Settings.cancel" />
            </Button>
            <Button
              variant={"lightblue"}
              onClick={handleSubmit}
              type="submit"
              loading={props.patching}
            >
              <FormattedMessage id="containers.Settings.save" />
            </Button>
          </div>
        </div>
      </form>
    </React.Fragment>
  );
};

export default connect(
  (state: ApplicationState) => ({
    loading: state.user.fetching || !state.user.fetched || !state.user.dirtyUserCreated,
    patching: state.user.patching,
    user: state.user.dirtyUser,
    error: state.user.error,
  }),
  { createDirtyUser, changeUserField, saveUser },
)(Settings);
